<template>
    <v-sheet class="bg-transparent mt-4">
        <div class="d-flex align-center mb-2" style="gap: 8px">
            <span class="text-subtitle-1 font-weight-bold">Condition Windows</span>
            <v-tooltip location="top" max-width="340">
                <template #activator="{ props }">
                    <v-icon v-bind="props" size="small" class="text-grey">mdi-information-outline</v-icon>
                </template>
                Damage on the selected target(s) while your chosen conditions were active, e.g. during each
                Pall of Ruination. Hits on an invincible target are left out, pets count for their owner.
            </v-tooltip>
            <v-spacer />
            <v-btn size="small" variant="text" prepend-icon="mdi-filter-cog" @click="managerOpen = true">Manage</v-btn>
            <v-btn size="small" variant="text" icon="mdi-refresh" :loading="loading" :disabled="!targetIds.length"
                title="Recalculate" @click="run" />
            <v-btn size="small" variant="text" :icon="collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                @click="collapsed = !collapsed" />
        </div>

        <template v-if="!collapsed">
            <v-alert v-if="!targetIds.length" type="info" variant="tonal" density="compact">
                Select a target to see condition windows.
            </v-alert>
            <v-alert v-else-if="!conditionConfigs.length" type="info" variant="tonal" density="compact">
                No conditions yet. Use Manage to add one.
            </v-alert>
            <v-alert v-else-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

            <div v-for="result in results" :key="result.configId" class="mb-4">
                <div class="d-flex align-center px-1 mb-1">
                    <span class="text-subtitle-2 font-weight-bold">{{ result.displayName || 'Unnamed' }}</span>
                    <v-spacer />
                    <span class="text-caption text-grey">
                        {{ result.groups.length }} windows · total {{ formatNumber(Math.round(result.totalDamage)) }}
                        · avg {{ formatNumber(Math.round(result.averageDamage)) }}
                    </span>
                </div>
                <div v-if="!result.groups.length" class="text-caption text-grey px-1">No matching hits.</div>

                <template v-else>
                    <div class="cw-row text-caption text-grey px-4 py-1 cw-head">
                        <div class="cw-idx">#</div>
                        <div class="cw-time">Time</div>
                        <div class="cw-dur">Duration</div>
                        <div class="cw-players">Players - share of the window's damage</div>
                        <div class="cw-total">Total</div>
                    </div>

                    <v-expansion-panels variant="accordion" flat class="cw-panels">
                        <v-expansion-panel v-for="group in result.groups" :key="group.index" class="bg-grey-darken-4">
                            <v-expansion-panel-title class="py-1 px-4">
                                <div class="cw-row" style="width: 100%">
                                    <div class="cw-idx font-weight-bold">{{ group.index }}</div>
                                    <div class="cw-time text-caption text-grey">
                                        {{ clock(group.startAt) }} – {{ clock(group.endAt) }}
                                    </div>
                                    <div class="cw-dur">{{ formatDuration(group.duration) }}</div>
                                    <div class="cw-players">
                                        <span v-for="col in columnsOf(result).filter(c => playerIn(group, c.id))" :key="col.id" class="cw-player">
                                            <span class="cw-dot" :style="{ background: playerColor(result, col.id) }"></span>
                                            {{ col.label }} - {{ pct(playerIn(group, col.id)!.totalDamage, group.totalDamage) }}
                                        </span>
                                    </div>
                                    <div class="cw-total font-weight-bold">
                                        {{ formatNumber(Math.round(group.totalDamage)) }}
                                        <div class="text-caption text-grey">{{ pct(group.totalDamage, result.totalDamage) }}</div>
                                    </div>
                                </div>
                            </v-expansion-panel-title>

                            <v-expansion-panel-text>
                                <div class="cw-prow text-caption text-grey py-1">
                                    <div>Player</div>
                                    <div class="text-right">Damage / share</div>
                                    <div class="text-right">Active / passive / pet</div>
                                    <div class="text-right">Crit</div>
                                </div>
                                <v-expansion-panels variant="accordion" flat>
                                    <v-expansion-panel v-for="p in group.players" :key="p.entityId" class="bg-transparent">
                                        <v-expansion-panel-title class="py-1 px-2 cw-share-row">
                                            <!-- Player's share of the window, like the Analysis player rows -->
                                            <div class="cw-share-bar" :style="{ width: pct(p.totalDamage, group.totalDamage), background: playerColor(result, p.entityId) }"></div>
                                            <div class="cw-prow" style="width: 100%">
                                                <div class="font-weight-medium text-truncate">{{ playerLabel(result, p.entityId) }}</div>
                                                <div class="text-right">
                                                    {{ formatNumber(Math.round(p.totalDamage)) }}
                                                    <span class="text-caption text-grey">{{ pct(p.totalDamage, group.totalDamage) }}</span>
                                                </div>
                                                <div class="text-right">
                                                    {{ pct(p.totalDamage - p.passiveDamage - (p.petDamage || 0), p.totalDamage) }}
                                                    <span class="text-grey">/</span>
                                                    {{ pct(p.passiveDamage, p.totalDamage) }}
                                                    <span class="text-grey">/</span>
                                                    {{ pct(p.petDamage || 0, p.totalDamage) }}
                                                </div>
                                                <div class="text-right">
                                                    {{ p.critEligible > 0 ? pct(p.critHits, p.critEligible) : '—' }}
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <v-row no-gutters class="pt-2">
                                                <v-col cols="12" md="auto" v-if="detailFor(group, p).statuses.length" class="pr-md-3">
                                                    <self-status-list :statuses="detailFor(group, p).statuses" title="Self Statuses"
                                                        :format-duration="formatDuration" :get-status-icon-url="getStatusIconUrl" />
                                                </v-col>
                                                <v-col cols="12" md style="min-width: 0">
                                                    <skill-breakdown :skills="detailFor(group, p).skillStats" title="Skill Breakdown"
                                                        :active-damage="detailFor(group, p).activeDamage"
                                                        :passive-damage="detailFor(group, p).passiveDamage"
                                                        :pet-damage="detailFor(group, p).petDamage"
                                                        :format-number="formatNumber" :get-skill-icon-url="getSkillIconUrl" :get-status-icon-url="getStatusIconUrl"
                                                        :get-skill-color="getSkillColor" :hex-to-rgba="hexToRgba" :region="region" />
                                                </v-col>
                                            </v-row>

                                            <v-btn size="x-small" variant="text" class="mt-1"
                                                :prepend-icon="showHits[hitKey(group, p)] ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                                @click="showHits[hitKey(group, p)] = !showHits[hitKey(group, p)]">
                                                Hit sequence ({{ p.hits.length }})
                                            </v-btn>
                                            <!-- Hit sequence -->
                                            <div v-if="showHits[hitKey(group, p)]" class="cw-hits thin-scroll">
                                                <div v-for="(h, i) in p.hits" :key="i" class="cw-hit text-body-2">
                                                    <span class="text-grey">{{ clock(h.at) }}</span>
                                                    <span class="text-truncate">
                                                        <span v-if="h.fromPet" class="text-caption text-green-lighten-2">Pet:</span>
                                                        {{ skillName(h.skillId) }}
                                                        <span v-if="h.triggerSkillId" class="text-caption text-grey">
                                                            from {{ skillName(h.triggerSkillId) }}
                                                        </span>
                                                    </span>
                                                    <span class="text-right">{{ formatNumber(Math.round(h.damage)) }}</span>
                                                    <span>
                                                        <v-icon v-if="h.isCritical" size="x-small" color="amber">mdi-lightning-bolt</v-icon>
                                                    </span>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Totals row -->
                    <div class="cw-row px-4 py-1 cw-foot">
                        <div class="cw-idx text-caption">Σ</div>
                        <div class="cw-time"></div>
                        <div class="cw-dur"></div>
                        <div class="cw-players font-weight-medium">
                            <span v-for="col in columnsOf(result)" :key="col.id" class="cw-player">
                                <span class="cw-dot" :style="{ background: playerColor(result, col.id) }"></span>
                                {{ col.label }} - {{ pct(col.total, result.totalDamage) }}
                            </span>
                        </div>
                        <div class="cw-total font-weight-bold">{{ formatNumber(Math.round(result.totalDamage)) }}</div>
                    </div>
                </template>
            </div>
        </template>

        <!-- Manager -->
        <v-dialog v-model="managerOpen" max-width="700" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon class="mr-2">mdi-filter-cog</v-icon>Condition Windows
                    <v-spacer />
                    <v-btn icon="mdi-close" variant="text" @click="managerOpen = false" />
                </v-card-title>
                <v-divider />
                <v-card-text style="max-height: 500px">
                    <div v-if="!conditionConfigs.length" class="text-center text-caption text-grey py-4">No conditions yet.</div>
                    <v-list density="compact" class="pa-0 bg-transparent">
                        <v-list-item v-for="(c, idx) in conditionConfigs" :key="c.id" class="px-0">
                            <template #prepend>
                                <div class="d-flex flex-column mr-1">
                                    <v-btn icon="mdi-chevron-up" size="x-small" variant="text" :disabled="idx === 0"
                                        @click="moveCondition(idx, idx - 1)" />
                                    <v-btn icon="mdi-chevron-down" size="x-small" variant="text"
                                        :disabled="idx === conditionConfigs.length - 1" @click="moveCondition(idx, idx + 1)" />
                                </div>
                            </template>
                            <div class="text-body-2 font-weight-medium">{{ c.displayName || 'Unnamed' }}</div>
                            <div class="text-caption text-grey">
                                {{ describeFilter(c.filter, condNames) }} · {{ c.groupBy === 'eachEntry' ? 'each entry' : 'total' }}
                            </div>
                            <template #append>
                                <v-btn icon="mdi-pencil" size="small" variant="text" @click="startEdit(c)" />
                                <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removeCondition(c.id)" />
                            </template>
                        </v-list-item>
                    </v-list>
                    <v-textarea v-if="importOpen" v-model="importText" label="Paste exported conditions (JSON)" rows="6"
                        class="mt-3" :error-messages="importError" />
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-btn size="small" variant="text" prepend-icon="mdi-export" @click="doExport">{{ copied ? 'Copied' : 'Export' }}</v-btn>
                    <v-btn v-if="!importOpen" size="small" variant="text" prepend-icon="mdi-import" @click="importOpen = true">Import</v-btn>
                    <v-btn v-else size="small" variant="tonal" prepend-icon="mdi-import" @click="doImport">Replace all</v-btn>
                    <v-spacer />
                    <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="startAdd">Add</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Editor -->
        <v-dialog v-model="editorOpen" max-width="620" scrollable>
            <v-card v-if="editing">
                <v-card-title>{{ editing.id ? 'Edit condition' : 'New condition' }}</v-card-title>
                <v-divider />
                <v-card-text style="max-height: 520px">
                    <v-text-field v-model="editing.displayName" label="Name" density="compact" variant="outlined"
                        placeholder="e.g. Burst during Pall" class="mb-3" hide-details />

                    <v-radio-group v-model="editing.groupBy" inline density="compact" hide-details class="mb-3">
                        <template #label><span class="text-body-2 mr-2">Windows</span></template>
                        <v-radio value="eachEntry" label="Each entry" />
                        <v-radio value="none" label="One total" />
                    </v-radio-group>
                    <div class="text-caption text-grey mb-3">
                        "Each entry" makes one window per entry into the first "While …" rule's condition;
                        without one, each run of continuous hits is a window.
                    </div>

                    <v-alert v-if="!editable" type="warning" variant="tonal" density="compact" class="mb-3">
                        This condition has nested rules the editor can't show ({{ describeFilter(editing.filter, condNames) }}).
                        You can still rename it or change its windows.
                    </v-alert>

                    <template v-else>
                        <div class="d-flex align-center mb-2">
                            <span class="text-body-2 font-weight-medium">Rules</span>
                            <v-btn-toggle v-if="rules.length > 1" v-model="combine" mandatory density="compact" class="ml-3" color="primary">
                                <v-btn value="and" size="small">All</v-btn>
                                <v-btn value="or" size="small">Any</v-btn>
                            </v-btn-toggle>
                        </div>
                        <div v-for="(rule, i) in rules" :key="i" class="d-flex align-center mb-2" style="gap: 8px">
                            <v-select v-model="rule.mode" :items="modeItems" density="compact" variant="outlined"
                                hide-details style="max-width: 200px" />
                            <v-autocomplete v-model="rule.conditionId" :items="conditionItems" :custom-filter="conditionSearch"
                                label="Condition" density="compact" variant="outlined" hide-details />
                            <v-btn icon="mdi-close" size="small" variant="text" :disabled="rules.length === 1" @click="rules.splice(i, 1)" />
                        </div>
                        <v-btn size="small" variant="text" prepend-icon="mdi-plus" @click="addRule">Add rule</v-btn>
                    </template>

                    <v-divider class="my-3" />
                    <div class="text-caption text-grey">Preview: {{ describeFilter(editedFilter, condNames) }}</div>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="editorOpen = false">Cancel</v-btn>
                    <v-btn color="primary" variant="flat" :disabled="!canSave" @click="saveEdit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-sheet>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch, inject, onUnmounted, type PropType, type Ref } from 'vue';
import {
    conditionConfigs, upsertCondition, removeCondition, moveCondition, exportConditions, importConditions,
    describeFilter, MODE_LABELS,
    type ConditionFilter, type ConditionFilterLeaf, type ConditionFilterMode, type ConditionGroup,
    type ConditionGroupPlayer, type ConditionResult, type CustomConditionConfig, type GroupByMode,
    windowSkillStats,
} from '@/conditionWindows';
import { anonymizeMode, currentSessionName, trackedStatuses } from '@/store';
import { formatNumber, formatDuration, getPlayerColor, getSkillColor, getSkillIconUrl, getStatusIconUrl, hexToRgba } from '@/util';
import { PASSIVE_SKILL_IDS } from '@/passiveSkills';
import SkillBreakdown from './SkillBreakdown.vue';
import SelfStatusList from './SelfStatusList.vue';

type EditingConfig = { id?: string; displayName: string; groupBy: GroupByMode; filter: ConditionFilter };

// Condition Windows section of the Analysis tab (ported from mabinogi-dilmeter's
// custom conditions). Results come from /api/combat/condition-windows.
export default defineComponent({
    name: 'ConditionWindows',
    components: { SkillBreakdown, SelfStatusList },
    props: {
        targetIds: { type: Array as PropType<string[]>, required: true },
        fromTime: { type: Number as PropType<number | undefined>, default: undefined },
        toTime: { type: Number as PropType<number | undefined>, default: undefined },
        // Times are shown relative to this (the combat start).
        combatStart: { type: Number, default: 0 },
        // Player ids in the Analysis tab's order: colors (and anonymized
        // numbers) follow it so each player looks the same in both places.
        playerOrder: { type: Array as PropType<string[]>, default: () => [] },
    },
    setup(props) {
        const skillNameMap = inject('skillNameMap') as Ref<Record<number, string>>;
        const condNameMap = inject('condNameMap') as Ref<Record<number, string> | undefined>;
        const condNames = computed(() => condNameMap?.value ?? {});
        const skillName = (id: number) => skillNameMap.value?.[id] ?? `Skill ${id}`;

        const COLLAPSE_KEY = 'funnycharts.conditionWindows.collapsed';
        const loadCollapsed = () => { try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; } };
        const collapsed = ref(loadCollapsed());
        watch(collapsed, v => { try { localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0'); } catch { /* ignore */ } });

        // ===== Results =====
        const results = ref<ConditionResult[]>([]);
        const loading = ref(false);
        const error = ref('');
        let requestSeq = 0;

        // The backend replays the whole session, so this runs on demand (and
        // debounced on input changes), never on live ticks.
        const run = async () => {
            if (!props.targetIds.length || !conditionConfigs.value.length) {
                results.value = [];
                return;
            }
            const seq = ++requestSeq;
            loading.value = true;
            error.value = '';
            try {
                const body: Record<string, unknown> = {
                    session: currentSessionName.value || '',
                    targets: props.targetIds,
                    configs: conditionConfigs.value,
                    trackedStatuses: trackedStatuses.value,
                };
                if (props.fromTime != null && props.toTime != null) {
                    body.from = props.fromTime;
                    body.to = props.toTime;
                }
                const r = await fetch('/api/combat/condition-windows', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
                const data = await r.json() as ConditionResult[];
                if (seq === requestSeq) results.value = data;
            } catch (e) {
                if (seq === requestSeq) error.value = `Failed to calculate condition windows: ${(e as Error).message}`;
            } finally {
                if (seq === requestSeq) loading.value = false;
            }
        };

        let debounce: ReturnType<typeof setTimeout> | null = null;
        const schedule = () => {
            if (collapsed.value) return;
            if (debounce) clearTimeout(debounce);
            debounce = setTimeout(run, 500);
        };
        watch(() => [props.targetIds.join(','), props.fromTime, props.toTime, currentSessionName.value, trackedStatuses.value.join(',')], schedule, { immediate: true });
        watch(conditionConfigs, schedule, { deep: true });
        watch(collapsed, v => { if (!v) schedule(); });
        onUnmounted(() => { if (debounce) clearTimeout(debounce); });

        // Analysis rank of a player; players missing there (no damage in the
        // Analysis view) come after, in condition damage order.
        const analysisRank = computed(() => new Map(props.playerOrder.map((id, i) => [id, i])));

        // Player columns per condition, ordered by total damage across its windows.
        type Column = { id: string; label: string; total: number; rank: number };
        const columns = computed(() => {
            const byConfig = new Map<string, Column[]>();
            for (const result of results.value) {
                const totals = new Map<string, { name: string; total: number }>();
                for (const g of result.groups) for (const p of g.players) {
                    const t = totals.get(p.entityId) ?? { name: p.name, total: 0 };
                    t.total += p.totalDamage;
                    totals.set(p.entityId, t);
                }
                let extra = props.playerOrder.length;
                const cols = [...totals.entries()]
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([id, t]) => {
                        const rank = analysisRank.value.get(id) ?? extra++;
                        return { id, total: t.total, rank, label: anonymizeMode.value ? `Player ${rank + 1}` : t.name };
                    });
                byConfig.set(result.configId, cols);
            }
            return byConfig;
        });
        const columnsOf = (result: ConditionResult): Column[] => columns.value.get(result.configId) ?? [];
        const playerLabel = (result: ConditionResult, id: string) => columnsOf(result).find(c => c.id === id)?.label ?? id;
        const playerIn = (group: ConditionGroup, id: string) => group.players.find(p => p.entityId === id);

        const region = inject('region') as unknown as string;
        // Same color per player as on the Analysis tab.
        const playerColor = (result: ConditionResult, id: string) =>
            getPlayerColor(columnsOf(result).find(c => c.id === id)?.rank ?? 0);

        // Expanded player: skill breakdown from the window's hits and the
        // backend's status uptime, filtered and named like the Analysis tab.
        // Cached per player object (each fetch brings new objects).
        type Detail = ReturnType<typeof windowSkillStats> & { statuses: any[] };
        const details = new WeakMap<ConditionGroupPlayer, Detail>();
        const detailFor = (group: ConditionGroup, p: ConditionGroupPlayer): Detail => {
            let d = details.get(p);
            if (!d) {
                const tracked = new Set(trackedStatuses.value);
                const statuses = (p.statusStats || [])
                    .filter((s: any) => tracked.has(s.id) && (s.uptimeRatio > 0 || s.overallRatio > 0))
                    .map((s: any) => {
                        const localName = condNames.value[s.id];
                        return { ...s, condName: localName || s.condName || `Status ${s.id}`, name: s.maxPercent ? s.name : (localName || s.name || `Status ${s.id}`) };
                    });
                d = { ...windowSkillStats(p.hits, group.duration, skillName, PASSIVE_SKILL_IDS), statuses };
                details.set(p, d);
            }
            return d;
        };
        const showHits = reactive<Record<string, boolean>>({});
        const hitKey = (group: ConditionGroup, p: ConditionGroupPlayer) => `${group.index}:${group.startAt}:${p.entityId}`;

        const pct = (part: number, whole: number) => `${whole > 0 ? ((part / whole) * 100).toFixed(1) : '0.0'}%`;
        const clock = (at: number) => {
            const s = Math.max(0, Math.floor(at - props.combatStart));
            return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
        };

        // ===== Manager =====
        const managerOpen = ref(false);
        const importOpen = ref(false);
        const importText = ref('');
        const importError = ref('');
        const copied = ref(false);
        const doExport = async () => {
            try {
                await navigator.clipboard.writeText(exportConditions());
                copied.value = true;
                setTimeout(() => { copied.value = false; }, 1500);
            } catch {
                importOpen.value = true;
                importText.value = exportConditions();
            }
        };
        const doImport = () => {
            if (!importConditions(importText.value)) {
                importError.value = 'Not a list of conditions.';
                return;
            }
            importError.value = '';
            importText.value = '';
            importOpen.value = false;
        };

        // ===== Editor =====
        // The editor shows one level of rules (a single rule, or All/Any of
        // several); deeper trees, e.g. imported from dilmeter, stay as they are.
        const editorOpen = ref(false);
        const editing = ref<EditingConfig | null>(null);
        const rules = ref<ConditionFilterLeaf[]>([]);
        const combine = ref<'and' | 'or'>('and');
        const editable = ref(true);

        const loadRules = (f: ConditionFilter) => {
            if (f.type === 'leaf') {
                rules.value = [{ ...f }];
                combine.value = 'and';
                editable.value = true;
            } else if (f.rules.every(r => r.type === 'leaf')) {
                rules.value = (f.rules as ConditionFilterLeaf[]).map(r => ({ ...r }));
                combine.value = f.type;
                editable.value = rules.value.length > 0;
                if (!rules.value.length) rules.value = [{ type: 'leaf', mode: 'onHit_targetHas', conditionId: 0 }];
            } else {
                rules.value = [];
                editable.value = false;
            }
        };
        const startEdit = (c: CustomConditionConfig) => {
            editing.value = JSON.parse(JSON.stringify(c));
            loadRules(c.filter);
            editorOpen.value = true;
        };
        const startAdd = () => {
            editing.value = { displayName: '', groupBy: 'eachEntry', filter: { type: 'leaf', mode: 'duringCondition_target', conditionId: 0 } };
            loadRules(editing.value.filter);
            editorOpen.value = true;
        };
        const addRule = () => rules.value.push({ type: 'leaf', mode: 'onHit_attackerHas', conditionId: 0 });

        const editedFilter = computed<ConditionFilter>(() => {
            if (!editing.value) return { type: 'and', rules: [] };
            if (!editable.value) return editing.value.filter;
            return rules.value.length === 1 ? rules.value[0] : { type: combine.value, rules: rules.value };
        });
        const canSave = computed(() => !!editing.value?.displayName.trim()
            && (!editable.value || rules.value.every(r => r.conditionId > 0)));
        const saveEdit = () => {
            if (!editing.value) return;
            upsertCondition({ ...editing.value, displayName: editing.value.displayName.trim(), filter: JSON.parse(JSON.stringify(editedFilter.value)) });
            editorOpen.value = false;
        };

        const modeItems = (Object.keys(MODE_LABELS) as ConditionFilterMode[]).map(m => ({ title: MODE_LABELS[m], value: m }));
        const conditionItems = computed(() => Object.entries(condNames.value)
            .map(([id, name]) => ({ title: `${name} (#${id})`, value: Number(id) }))
            .sort((a, b) => a.value - b.value));
        const conditionSearch = (value: string, query: string) =>
            value.toLowerCase().includes(query.toLowerCase());

        return {
            region, playerColor, detailFor, showHits, hitKey,
            getSkillColor, getSkillIconUrl, getStatusIconUrl, hexToRgba,
            conditionConfigs, moveCondition, removeCondition, describeFilter, condNames, skillName,
            collapsed, results, loading, error, run,
            columnsOf, playerLabel, playerIn, pct, clock, formatNumber, formatDuration,
            managerOpen, importOpen, importText, importError, copied, doExport, doImport,
            editorOpen, editing, rules, combine, editable, startEdit, startAdd, addRule,
            editedFilter, canSave, saveEdit, modeItems, conditionItems, conditionSearch,
        };
    },
});
</script>

<style scoped>
.cw-row { display: flex; align-items: center; }
.cw-idx { width: 32px; }
.cw-time { width: 120px; }
.cw-dur { width: 72px; text-align: right; padding-right: 8px; }
.cw-players { flex: 1; min-width: 0; display: flex; flex-wrap: wrap; gap: 2px 16px; padding: 0 8px; }
.cw-player { white-space: nowrap; }
.cw-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 2px; }
.cw-total { width: 110px; text-align: right; }
/* Header/footer line up with panel titles, which reserve space for the chevron. */
.cw-head, .cw-foot { padding-right: 56px !important; }
.cw-head { border-bottom: 1px solid rgba(128, 128, 128, 0.25); }
.cw-foot { border-top: 2px solid rgba(128, 128, 128, 0.25); }
.cw-prow { display: grid; grid-template-columns: 2fr 2fr 2fr 1fr; gap: 8px; align-items: center; padding-right: 40px; }
.cw-hits { max-height: 320px; overflow-y: auto; }
/* Share as a thin bar along the bottom of the row, as on the Analysis tab. */
.cw-share-row { position: relative; }
.cw-share-bar { position: absolute; left: 0; bottom: 0; height: 3px; border-radius: 0 2px 2px 0; pointer-events: none; }
.cw-hit { display: grid; grid-template-columns: 56px 1fr 110px 24px; gap: 8px; padding: 1px 0; }
.thin-scroll::-webkit-scrollbar { width: 6px; }
.thin-scroll::-webkit-scrollbar-thumb { background-color: rgba(255, 255, 255, 0.2); border-radius: 3px; }
</style>
